import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Send, X, Users as UsersIcon } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api';
import { PageHeader, SectionCard, EmptyState, LoadingSpinner } from '@/components/shared/DashboardUI';
import { formatDate } from '../lib/formatters';


const StatusBadge = ({ status }) => {
  const color = status === 'Active' ? '#16a34a' : status === 'Inactive' ? '#94a3b8' : '#2563eb';
  const bg = status === 'Active' ? '#dcfce7' : status === 'Inactive' ? '#e2e8f0' : '#dbeafe';
  return (
    <span data-testid={`team-member-status-${status.toLowerCase()}`} style={{
      display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: '999px',
      background: bg, color, fontSize: '0.72rem', fontWeight: 600,
    }}>{status}</span>
  );
};

const TeamMemberFormModal = ({ open, initial, onClose, onSubmit, saving }) => {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  useEffect(() => {
    if (open) setForm({
      first_name: initial?.first_name || '',
      last_name: initial?.last_name || '',
      email: initial?.email || '',
      phone: initial?.phone || '',
    });
  }, [open, initial]);
  if (!open) return null;
  const isEdit = !!initial;
  return (
    <div data-testid="team-member-modal" style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: '12px', maxWidth: '480px', width: '100%',
        padding: '1.25rem', boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.85rem' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: 'var(--mcl-text-heading)' }}>
            {isEdit ? 'Edit Team Member' : 'Add Team Member'}
          </h3>
          <button onClick={onClose} className="mcl-btn mcl-btn-sm mcl-btn-ghost" data-testid="close-tm-modal" aria-label="Close"><X size={16} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}>
          <div className="mcl-form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="mcl-form-group">
              <label className="mcl-form-label">First Name *</label>
              <input className="mcl-form-input" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="tm-first-name" />
            </div>
            <div className="mcl-form-group">
              <label className="mcl-form-label">Last Name *</label>
              <input className="mcl-form-input" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="tm-last-name" />
            </div>
          </div>
          <div className="mcl-form-group">
            <label className="mcl-form-label">Email *</label>
            <input className="mcl-form-input" type="email" required disabled={isEdit} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="tm-email" />
            {isEdit && <span style={{ fontSize: '0.72rem', color: 'var(--mcl-text-muted)' }}>Email cannot be changed after creation.</span>}
          </div>
          <div className="mcl-form-group">
            <label className="mcl-form-label">Phone</label>
            <input className="mcl-form-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="tm-phone" />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
            <button type="button" className="mcl-btn mcl-btn-outline" onClick={onClose} data-testid="cancel-tm-modal">Cancel</button>
            <button type="submit" className="mcl-btn mcl-btn-primary" disabled={saving} data-testid="submit-tm-modal">
              {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create & Invite')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TeamMembersTab = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/team-members');
      setMembers(data.team_members || []);
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchMembers(); }, []);

  const handleSubmit = async (form) => {
    setSaving(true);
    try {
      if (editing) {
        await apiPut(`/team-members/${editing.user_id}`, {
          first_name: form.first_name, last_name: form.last_name, phone: form.phone,
        });
        toast.success('Team member updated');
      } else {
        await apiPost('/team-members', { ...form, send_invite: true });
        toast.success('Team member invited');
      }
      setShowModal(false); setEditing(null);
      await fetchMembers();
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  const handleDeactivate = async (m) => {
    if (!window.confirm(`Deactivate ${m.first_name} ${m.last_name}? They'll lose access immediately.`)) return;
    try {
      await apiDelete(`/team-members/${m.user_id}`);
      toast.success('Team member deactivated');
      await fetchMembers();
    } catch (e) { toast.error(e.message); }
  };

  const handleResendInvite = async (m) => {
    try {
      await apiPost(`/team-members/${m.user_id}/resend-invite`);
      toast.success('Invite re-sent');
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div data-testid="team-members-tab">
      <PageHeader
        title="Team Members"
        subtitle="Add paralegals or support staff to help review and manage applications."
        actions={
          <button className="mcl-btn mcl-btn-primary" onClick={() => { setEditing(null); setShowModal(true); }} data-testid="add-team-member-btn">
            <Plus size={14} /> Add Team Member
          </button>
        }
      />

      <SectionCard title="Your Team">
        {members.length === 0 ? (
          <EmptyState
            icon={UsersIcon}
            title="No team members yet"
            text="Add paralegals or support staff so they can help you review applications and stay in the loop on funding offers."
            actions={
              <button className="mcl-btn mcl-btn-primary" onClick={() => { setEditing(null); setShowModal(true); }} data-testid="empty-add-tm-btn">
                <Plus size={14} /> Add your first team member
              </button>
            }
          />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="mcl-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Name', 'Email', 'Phone', 'Status', 'Created', ''].map((h) => (
                    <th key={h} style={{ textAlign: 'left', padding: '0.6rem 0.5rem', borderBottom: '1px solid var(--mcl-border)', fontSize: '0.78rem', color: 'var(--mcl-text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.user_id} data-testid={`team-member-row-${m.user_id}`}>
                    <td style={{ padding: '0.6rem 0.5rem', borderBottom: '1px solid var(--mcl-border-light)', fontWeight: 600 }}>{m.first_name} {m.last_name}</td>
                    <td style={{ padding: '0.6rem 0.5rem', borderBottom: '1px solid var(--mcl-border-light)', color: 'var(--mcl-text-body)' }}>{m.email}</td>
                    <td style={{ padding: '0.6rem 0.5rem', borderBottom: '1px solid var(--mcl-border-light)', color: 'var(--mcl-text-muted)' }}>{m.phone || '—'}</td>
                    <td style={{ padding: '0.6rem 0.5rem', borderBottom: '1px solid var(--mcl-border-light)' }}><StatusBadge status={m.status} /></td>
                    <td style={{ padding: '0.6rem 0.5rem', borderBottom: '1px solid var(--mcl-border-light)', color: 'var(--mcl-text-muted)', fontSize: '0.85rem' }}>{formatDate(m.created_at)}</td>
                    <td style={{ padding: '0.6rem 0.5rem', borderBottom: '1px solid var(--mcl-border-light)', textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '0.3rem' }}>
                        {m.status === 'Invited' && (
                          <button className="mcl-btn mcl-btn-sm mcl-btn-outline" onClick={() => handleResendInvite(m)} data-testid={`resend-invite-${m.user_id}`} title="Resend invite">
                            <Send size={13} /> Resend
                          </button>
                        )}
                        <button className="mcl-btn mcl-btn-sm mcl-btn-ghost" onClick={() => { setEditing(m); setShowModal(true); }} data-testid={`edit-tm-${m.user_id}`} aria-label="Edit"><Pencil size={13} /></button>
                        {!m.disabled && (
                          <button className="mcl-btn mcl-btn-sm mcl-btn-ghost" onClick={() => handleDeactivate(m)} data-testid={`deactivate-tm-${m.user_id}`} aria-label="Deactivate" style={{ color: 'var(--mcl-danger)' }}><Trash2 size={13} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <TeamMemberFormModal
        open={showModal}
        initial={editing}
        saving={saving}
        onClose={() => { setShowModal(false); setEditing(null); }}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export { TeamMembersTab, TeamMemberFormModal };
export default TeamMembersTab;
